import React, { useRef, useState } from "react";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { Upload, Paperclip, CheckCircle2, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable receipt/file uploader. Used by Reimbursements and Travel line items.
 * Hits POST /api/upload and surfaces the returned URL via onChange.
 */
export default function ReceiptUploader({ value, onChange, testid, compact = false }) {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [originalName, setOriginalName] = useState("");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      setOriginalName(data.original_name);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (value) {
    const isImage = /\.(jpg|jpeg|png|webp|gif|heic|heif)$/i.test(value);
    return (
      <div className={`flex items-center gap-2 bg-[#f6f6f6] border border-[#d9d9d9] rounded-[10px] ${compact ? "p-1.5" : "p-3"}`}>
        {isImage ? (
          <img src={`${BACKEND_URL}${value}`} alt="receipt" className={`${compact ? "w-8 h-8" : "w-14 h-14"} object-cover rounded-[6px] border border-[#eeeded]`} />
        ) : (
          <div className={`${compact ? "w-8 h-8" : "w-14 h-14"} rounded-[6px] bg-white border border-[#eeeded] flex items-center justify-center`}>
            <Paperclip size={compact ? 12 : 18} className="text-[#475569]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {!compact && (
            <div className="flex items-center gap-1.5 text-[#008932] text-[13px] font-medium">
              <CheckCircle2 size={14} /> Receipt attached
            </div>
          )}
          <a
            href={`${BACKEND_URL}${value}`}
            target="_blank"
            rel="noreferrer"
            className={`${compact ? "text-[11px]" : "text-[12px]"} text-[#475569] hover:underline truncate block`}
            data-testid={testid ? `${testid}-link` : undefined}
          >
            {originalName || value.split("/").pop()}
          </a>
        </div>
        <button
          type="button"
          onClick={() => { onChange(""); setOriginalName(""); }}
          className="text-[#881a27] hover:bg-[#e5c1c1] p-1 rounded"
          data-testid={testid ? `${testid}-remove` : undefined}
        >
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        data-testid={testid}
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className={`w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#d9d9d9] hover:border-[#475569] hover:bg-[#f6f6f6] rounded-[10px] ${compact ? "px-2 py-1.5 text-[11px]" : "px-4 py-4 text-[13px]"} text-[#747474] hover:text-[#475569] transition-all cursor-pointer`}
      >
        <Upload size={compact ? 12 : 16} />
        {uploading ? "..." : (compact ? t("travel.attach_receipt") : t("travel.tap_upload"))}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.pdf,.heic,.heif"
        onChange={onPick}
        className="hidden"
      />
      {error && <div className="text-[11px] text-[#881a27] mt-1">{error}</div>}
      {!compact && <div className="text-[11px] text-[#747474] mt-1">JPG, PNG, PDF, HEIC up to 10MB</div>}
    </div>
  );
}
